/**
 * Demo data store — the single mutable source of truth in demo mode.
 *
 * Seeded from `seed.ts` (deterministic), but unlike the raw seed arrays this
 * store is MUTABLE and REACTIVE: adding a product, placing an order, reserving
 * a unit, or a live-sync tick all mutate it and notify subscribers. The query
 * layer (`queries.ts`) reads from here and invalidates on the `bump` signal so
 * the UI refreshes automatically.
 *
 * A real backend would replace these methods with API calls; the UI is unchanged.
 */

import { create } from "zustand";
import {
  AUDIT as SEED_AUDIT,
  ORDERS as SEED_ORDERS,
  PRODUCTS as SEED_PRODUCTS,
  RESELLERS,
  DEMO_NOW,
} from "./seed";
import type { AuditEntry, Order, Product, Reservation, SizeStock } from "@/types/domain";

let clock = DEMO_NOW.getTime();
/** Monotonic timestamp — advances past DEMO_NOW so new events sort newest-first. */
function now(): string {
  clock += 37_000; // ~37s between synthetic events, deterministic
  return new Date(clock).toISOString();
}

function uid(prefix: string): string {
  return `${prefix}-${(clock += 1).toString(36)}`;
}

interface DemoStoreState {
  products: Product[];
  orders: Order[];
  reservations: Reservation[];
  audit: AuditEntry[];
  /** Increments on every mutation; queries watch it to know when to refetch. */
  version: number;

  // Product mutations
  upsertProduct: (input: ProductInput) => Product;
  adjustSize: (productId: string, size: string, delta: number) => void;

  // Reservation + order lifecycle
  reserve: (productId: string, size: string, qty: number, resellerId: string) => Reservation | null;
  releaseReservation: (reservationId: string) => void;
  checkout: (reservationId: string) => Order | null;
  setOrderStatus: (orderId: string, status: Order["status"]) => void;

  // Live sync
  applyTick: (events: TickEvent[]) => void;

  reset: () => void;
}

export interface ProductInput {
  id?: string;
  wholesalerId: string;
  wholesalerName: string;
  name: string;
  brand: string;
  category: "sneakers" | "official" | "casual" | "lifestyle";
  description?: string;
  condition?: "new" | "like_new" | "good" | "fair";
  price: number;
  visible: boolean;
  sizes: SizeStock[];
  imageColor?: string;
}

export interface TickEvent {
  productId: string;
  size: string;
  delta: number; // negative = a sale elsewhere, positive = restock
}

const CHART_ACCENTS = [
  "oklch(0.58 0.11 185)",
  "oklch(0.62 0.14 255)",
  "oklch(0.70 0.15 75)",
  "oklch(0.62 0.16 330)",
  "oklch(0.64 0.14 145)",
];

function logEntry(audit: AuditEntry[], entry: Omit<AuditEntry, "id" | "at">): AuditEntry[] {
  return [{ ...entry, id: uid("a"), at: now() }, ...audit].slice(0, 200);
}

export const useDemoStore = create<DemoStoreState>((set, get) => ({
  // Deep-clone the seed so we never mutate the shared seed arrays.
  products: SEED_PRODUCTS.map((p) => ({ ...p, sizes: p.sizes.map((s) => ({ ...s })) })),
  orders: SEED_ORDERS.map((o) => ({ ...o })),
  reservations: [],
  audit: SEED_AUDIT.map((a) => ({ ...a })),
  version: 0,

  upsertProduct: (input) => {
    const state = get();
    const existing = input.id ? state.products.find((p) => p.id === input.id) : undefined;
    const product: Product = {
      id: input.id ?? uid("p"),
      wholesalerId: input.wholesalerId,
      wholesalerName: input.wholesalerName,
      name: input.name,
      brand: input.brand,
      category: input.category,
      description: input.description ?? existing?.description ?? "",
      condition: input.condition ?? existing?.condition ?? "new",
      imageColor:
        input.imageColor ??
        existing?.imageColor ??
        CHART_ACCENTS[state.products.length % CHART_ACCENTS.length],
      imageUrls: existing?.imageUrls ?? [],
      sizes: input.sizes.map((s) => ({ ...s })),
      price: input.price,
      visible: input.visible,
      updatedAt: now(),
    };
    const products = existing
      ? state.products.map((p) => (p.id === product.id ? product : p))
      : [product, ...state.products];
    const audit = logEntry(state.audit, {
      actor: input.wholesalerName,
      actorRole: "wholesaler",
      action: existing ? "product.update" : "product.create",
      target: `${product.brand} ${product.name}`,
      newData: existing ? { status: "updated" } : { status: "created" }, oldData: null, recordId: null,
    });
    set({ products, audit, version: state.version + 1 });
    return product;
  },

  adjustSize: (productId, size, delta) => {
    const state = get();
    const products = state.products.map((p) => {
      if (p.id !== productId) return p;
      const sizes = p.sizes.map((s) =>
        s.size === size ? { ...s, qty: Math.max(0, s.qty + delta) } : s,
      );
      return { ...p, sizes, updatedAt: now() };
    });
    set({ products, version: state.version + 1 });
  },

  reserve: (productId, size, qty, resellerId) => {
    const state = get();
    const product = state.products.find((p) => p.id === productId);
    if (!product) return null;
    const stock = product.sizes.find((s) => s.size === size);
    // Overselling guard: only what's genuinely available (net of active holds).
    const held = state.reservations
      .filter((r) => r.productId === productId && r.size === size && r.status === "held")
      .reduce((sum, r) => sum + r.qty, 0);
    const available = (stock?.qty ?? 0) - held;
    if (available < qty) return null;

    const reseller = RESELLERS.find((r) => r.id === resellerId);
    const reservation: Reservation = {
      id: uid("res"),
      productId,
      productName: product.name,
      brand: product.brand,
      wholesalerId: product.wholesalerId,
      wholesalerName: product.wholesalerName,
      resellerId,
      resellerName: reseller?.name ?? "You",
      size,
      qty,
      unitPrice: product.price,
      commissionRate: reseller?.commissionRate ?? 0.03,
      status: "held",
      createdAt: now(),
      expiresAt: new Date(clock + 15 * 60_000).toISOString(),
    };
    set({ reservations: [reservation, ...state.reservations], version: state.version + 1 });
    return reservation;
  },

  releaseReservation: (reservationId) => {
    const state = get();
    const reservations = state.reservations.map((r) =>
      r.id === reservationId && r.status === "held" ? { ...r, status: "released" as const } : r,
    );
    set({ reservations, version: state.version + 1 });
  },

  checkout: (reservationId) => {
    const state = get();
    const res = state.reservations.find((r) => r.id === reservationId);
    if (!res || res.status !== "held") return null;

    // Decrement real stock now that the sale is committed.
    const products = state.products.map((p) => {
      if (p.id !== res.productId) return p;
      const sizes = p.sizes.map((s) =>
        s.size === res.size ? { ...s, qty: Math.max(0, s.qty - res.qty) } : s,
      );
      return { ...p, sizes, updatedAt: now() };
    });

    const order: Order = {
      id: uid("ord"),
      reference: `SS-${2000 + state.orders.length}`,
      wholesalerId: res.wholesalerId,
      wholesalerName: res.wholesalerName,
      resellerId: res.resellerId,
      resellerName: res.resellerName,
      productId: res.productId,
      productName: res.productName,
      brand: res.brand,
      size: res.size,
      qty: res.qty,
      unitPrice: res.unitPrice,
      commissionRate: res.commissionRate,
      status: "pending",
      createdAt: now(),
    };

    const reservations = state.reservations.map((r) =>
      r.id === reservationId ? { ...r, status: "checked_out" as const } : r,
    );
    const audit = logEntry(state.audit, {
      actor: res.resellerName,
      actorRole: "reseller",
      action: "INSERT",
      target: "orders",
      recordId: null, oldData: null, newData: { status: "pending" },
    });
    set({ products, orders: [order, ...state.orders], reservations, audit, version: state.version + 1 });
    return order;
  },

  setOrderStatus: (orderId, status) => {
    const state = get();
    let changed = false;
    const orders = state.orders.map((o) => {
      if (o.id !== orderId) return o;
      changed = true;
      return { ...o, status };
    });
    if (!changed) return;
    const target = state.orders.find((o) => o.id === orderId);
    const audit = target
      ? logEntry(state.audit, {
          actor: target.wholesalerName,
          actorRole: "wholesaler",
          action: "UPDATE",
          target: "orders",
          recordId: null, oldData: { status: target.status }, newData: { status },
        })
      : state.audit;
    set({ orders, audit, version: state.version + 1 });
  },

  applyTick: (events) => {
    const state = get();
    let changed = false;
    const products = state.products.map((p) => {
      const evs = events.filter((e) => e.productId === p.id);
      if (!evs.length) return p;
      changed = true;
      const sizes = p.sizes.map((s) => {
        const ev = evs.find((e) => e.size === s.size);
        return ev ? { ...s, qty: Math.max(0, s.qty + ev.delta) } : s;
      });
      return { ...p, sizes, updatedAt: now() };
    });
    if (!changed) return;
    set({ products, version: state.version + 1 });
  },

  reset: () =>
    set({
      products: SEED_PRODUCTS.map((p) => ({ ...p, sizes: p.sizes.map((s) => ({ ...s })) })),
      orders: SEED_ORDERS.map((o) => ({ ...o })),
      reservations: [],
      audit: SEED_AUDIT.map((a) => ({ ...a })),
      version: 0,
    }),
}));

/** Non-hook accessor for query functions. */
export function storeState() {
  return useDemoStore.getState();
}

/** Available units of a size, net of active holds — the overselling guard. */
export function availableQty(productId: string, size: string): number {
  const s = storeState();
  const stock = s.products.find((p) => p.id === productId)?.sizes.find((z) => z.size === size);
  const held = s.reservations
    .filter((r) => r.productId === productId && r.size === size && r.status === "held")
    .reduce((sum, r) => sum + r.qty, 0);
  return Math.max(0, (stock?.qty ?? 0) - held);
}
